import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import ConfirmDialog from './ConfirmDialog';

export interface ConfirmOptions {
  title?: string;
  description?: string;
  /** The record being acted on, shown by name. */
  subject?: string;
  /** What will be lost, one short line each. */
  consequences?: string[];
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Promise-based confirmation, so a call site reads almost exactly like the
 * `window.confirm` it replaces:
 *
 *   if (!(await confirm({ title: 'Delete X?' }))) return;
 *
 * That shape matters. The declarative <ConfirmDialog> already existed, but
 * using it means hoisting "which thing am I confirming" into component state
 * and splitting the handler in two — which is why ~14 call sites had quietly
 * stayed on window.confirm and shipped a raw "ans.ainaeco.uk says" browser
 * dialog to users instead.
 */
export const ConfirmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    return new Promise<boolean>((resolve) => { resolverRef.current = resolve; });
  }, []);

  // Settle the promise exactly once, whichever way the dialog closes — an
  // unresolved promise here would silently hang the caller's handler forever.
  const settle = (value: boolean) => {
    setOptions(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(value);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <ConfirmDialog
        open={options !== null}
        onClose={() => settle(false)}
        onConfirm={() => settle(true)}
        title={options?.title ?? 'Are you sure?'}
        description={options?.description}
        subject={options?.subject}
        consequences={options?.consequences}
        confirmLabel={options?.confirmLabel ?? 'Confirm'}
        cancelLabel={options?.cancelLabel ?? 'Cancel'}
        variant={options?.variant ?? 'danger'}
      />
    </ConfirmContext.Provider>
  );
};

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider');
  return ctx;
}

export default ConfirmProvider;
